/**
 * Locale primitives, deliberately free of the compiled message catalogs.
 *
 * `i18n.ts` imports both `.po` files at module scope so `activateLocale` can
 * be synchronous. That side effect makes it unimportable from anywhere the
 * `.po` transform isn't configured — notably the Vitest frontend project,
 * which inherits the root Vite plugins but still parses `.po` as JavaScript.
 *
 * So anything pure lives here instead, and `i18n.ts` re-exports it. That
 * keeps `resolveLocale` (the marketing → app language handoff) unit-testable
 * without dragging two catalogs into the test runner.
 */

/** Locales the app ships translations for. */
export const LOCALES = ['pt-BR', 'en'] as const
export type Locale = (typeof LOCALES)[number]

/** Locale shown to a brand-new user before they pick one. */
export const DEFAULT_LOCALE: Locale = 'pt-BR'

/** Human-readable names for a language picker. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  'pt-BR': 'Português (Brasil)',
}

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value)

/** localStorage key — mirrors the `planno-theme` convention. */
export const LOCALE_STORAGE_KEY = 'planno-locale'

/**
 * Query parameter that carries a language across the marketing → app border.
 *
 * The marketing surface decides its language from the URL (`/` is pt-BR,
 * `/en/**` is English) and deliberately never reads the stored app
 * preference — see `src/lib/marketing-locale.tsx`. The app decides from
 * `localStorage`. Without a bridge, a visitor who arrives on `/en` and clicks
 * "Get started" lands on a Portuguese sign-up page, because they have no
 * stored preference and the default is pt-BR.
 *
 * `?lang=` is that bridge. `<CtaButton/>` appends it to every app-bound link
 * on the marketing pages, so the language you were reading in is the language
 * you land in.
 */
export const LOCALE_QUERY_PARAM = 'lang'

export type LocaleSource = 'url' | 'stored' | 'default'

/**
 * Resolves the locale the app should boot in, in precedence order:
 * an explicit `?lang=`, then the stored preference, then the default.
 *
 * Pure so it can be tested without a DOM. Both inputs are the raw untrusted
 * strings — anything unrecognised falls through to the next source rather
 * than throwing, because a bad `?lang=` in a shared link must not break the
 * page.
 */
export const resolveLocale = ({
  search,
  stored,
}: {
  /** `window.location.search`, including the leading `?`. */
  search: string
  /** Raw `localStorage` value, or `null` when unset/unavailable. */
  stored: string | null
}): { locale: Locale; source: LocaleSource } => {
  const fromUrl = new URLSearchParams(search).get(LOCALE_QUERY_PARAM)
  if (isLocale(fromUrl)) {
    return { locale: fromUrl, source: 'url' }
  }
  if (isLocale(stored)) {
    return { locale: stored, source: 'stored' }
  }
  return { locale: DEFAULT_LOCALE, source: 'default' }
}

/** Raw stored preference. `null` on the server and when storage is denied. */
export const readStoredLocale = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    // Private-mode storage denial — treat as unset.
    return null
  }
}

export const persistLocale = (locale: Locale) => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Nothing to do — the locale still applies for this page view.
  }
}
